import { createEffect, createSignal, onCleanup } from 'solid-js'
import { getGsap, prefersReducedMotion } from '~/utils/gsap'

// Tipo estructural mínimo para no acoplar el hook a los tipos internos de GSAP.
// El hook sólo necesita registrar `onComplete`, por lo que no expone el resto
// de callbacks de la librería en su contrato público.
type Anim = {
  kill: () => void
  eventCallback: (type: 'onComplete', callback: () => void) => unknown
}

export interface PresenceOptions {
  /** Señal reactiva: ¿debería estar abierto/visible? */
  open: () => boolean
  /** Timeline/tween de entrada. Recibe el nodo ya montado. */
  enter: (el: HTMLElement) => Anim | undefined
  /** Timeline/tween de salida. El nodo permanece montado hasta que termine. */
  exit: (el: HTMLElement) => Anim | undefined
  onEntered?: () => void
  onExited?: () => void
}

/**
 * Coordina animaciones GSAP con el renderizado condicional de Solid.
 *
 * `mounted()` controla un <Show when={mounted()}>: al abrir, monta primero y luego
 * anima; al cerrar, ejecuta la salida y solo desmonta en el onComplete del timeline
 * (sin setTimeout adivinados). Mata animaciones en vuelo ante toggles rápidos y
 * resuelve instantáneamente si el usuario prefiere movimiento reducido.
 */
export function usePresence(opts: PresenceOptions) {
  const [mounted, setMounted] = createSignal(opts.open())
  let el: HTMLElement | undefined
  let anim: Anim | undefined

  const setRef = (node: HTMLElement) => {
    el = node
  }

  createEffect(() => {
    const isOpen = opts.open()
    const g = getGsap()

    if (isOpen) {
      setMounted(true)
      // El ref se asigna tras montar: esperamos al microtask para tenerlo.
      queueMicrotask(() => {
        if (!el) return
        anim?.kill()
        if (!g || prefersReducedMotion()) {
          opts.onEntered?.()
          return
        }
        const a = opts.enter(el)
        anim = a || undefined
        anim?.eventCallback('onComplete', () => opts.onEntered?.())
      })
    } else if (mounted()) {
      const done = () => {
        setMounted(false)
        opts.onExited?.()
      }
      if (!el || !g || prefersReducedMotion()) {
        done()
        return
      }
      anim?.kill()
      const a = opts.exit(el)
      anim = a || undefined
      if (anim) anim.eventCallback('onComplete', done)
      else done()
    }
  })

  onCleanup(() => anim?.kill())

  return { mounted, setRef }
}
